import React, { useCallback, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from 'authConfig';
import { apiPath, requiredGroupId, adminGroupId, scopes } from 'apiurl';
import { useAuth } from 'AuthProvider';
import '../assets/css/AccessRequests.css';

const STATUS_META = {
  pending:  { label: 'Pending',  cls: 'ar-badge ar-badge--pending'  },
  approved: { label: 'Approved', cls: 'ar-badge ar-badge--approved' },
  denied:   { label: 'Denied',   cls: 'ar-badge ar-badge--denied'   },
};

const FILTERS = ['all', 'pending', 'approved', 'denied'];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC',
  });
}

async function fetchGroupMembers(token, groupId) {
  const url = `https://graph.microsoft.com/v1.0/groups/${groupId}/members?$select=id,displayName,mail,userPrincipalName`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Graph API error ${res.status}`);
  const data = await res.json();
  return data.value || [];
}

export default function AccessRequests() {
  const { role } = useAuth();
  const { instance, accounts } = useMsal();

  // ── Requests state ──
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [filter,   setFilter]   = useState('all');
  const [search,   setSearch]   = useState('');
  const [updating, setUpdating] = useState(null);
  const [expanded, setExpanded] = useState(null);

  // ── Members state ──
  const [view,           setView]          = useState('requests'); // 'requests' | 'members'
  const [members,        setMembers]       = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError,   setMembersError]  = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${apiPath}/api/access-requests`);
      setRequests(data);
    } catch {
      setError('Failed to load access requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    setMembersLoading(true);
    setMembersError(null);
    try {
      // Resolve group IDs: prefer baked-in env vars, fall back to server config
      let resolvedRequired = requiredGroupId;
      let resolvedAdmin    = adminGroupId;
      if (!resolvedRequired) {
        const cfg = await axios.get(`${apiPath}/api/config/groups`);
        resolvedRequired = cfg.data.requiredGroupId;
        resolvedAdmin    = cfg.data.adminGroupId;
      }
      if (!resolvedRequired) {
        setMembersError('PASS group ID is not configured on the server (AZURE_REQUIRED_GROUP_ID missing).');
        return;
      }

      // Acquire Graph token directly — same pattern as AuthProvider's acquireProfileToken
      const activeAccount = instance.getActiveAccount() || accounts?.[0];
      if (!activeAccount) {
        setMembersError('No active account. Please sign out and sign in again.');
        return;
      }
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: activeAccount,
        scopes,
      });
      const token = tokenResponse.accessToken;

      // Fetch PASS-group members + PASS-admins members in parallel
      const [passMembers, adminMembers] = await Promise.all([
        fetchGroupMembers(token, resolvedRequired),
        resolvedAdmin ? fetchGroupMembers(token, resolvedAdmin) : Promise.resolve([]),
      ]);

      const adminIds = new Set(adminMembers.map(m => m.id));
      const merged = passMembers.map(m => ({
        id:          m.id,
        displayName: m.displayName || '—',
        email:       m.mail || m.userPrincipalName || '—',
        memberRole:  adminIds.has(m.id) ? 'admin' : 'user',
      }));

      // Admins first, then users; alphabetical within each group
      merged.sort((a, b) => {
        if (a.memberRole !== b.memberRole) return a.memberRole === 'admin' ? -1 : 1;
        return a.displayName.localeCompare(b.displayName);
      });

      setMembers(merged);
    } catch (err) {
      setMembersError(`Failed to load group members: ${err.message}`);
    } finally {
      setMembersLoading(false);
    }
  }, [instance, accounts]);

  // Only fetch once role is confirmed as admin — prevents a non-admin from
  // triggering the API call before the component's redirect fires.
  useEffect(() => {
    if (role === 'admin') fetchRequests();
  }, [fetchRequests, role]);

  // Fetch members when the Members tab is first opened
  useEffect(() => {
    if (view === 'members' && members.length === 0 && !membersLoading && !membersError) {
      fetchMembers();
    }
  }, [view, members.length, membersLoading, membersError, fetchMembers]);

  // role is null/undefined while auth is still resolving; don't redirect prematurely
  if (role != null && role !== 'admin') return <Redirect to="/pass/dashboard" />;

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      await axios.patch(`${apiPath}/api/access-requests/${id}`, { status });
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch {
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  const visible = requests.filter(r => {
    if (filter !== 'all' && r.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        r.fullName.toLowerCase().includes(q) ||
        r.workEmail.toLowerCase().includes(q) ||
        r.organizationName.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const counts = {
    all:      requests.length,
    pending:  requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    denied:   requests.filter(r => r.status === 'denied').length,
  };

  return (
    <div className="ar-page">

      {/* ── Header ── */}
      <div className="ar-header">
        <div>
          <h1 className="ar-title">
            {view === 'members' ? 'Group Members' : 'Access Requests'}
          </h1>
          <p className="ar-subtitle">
            {view === 'members'
              ? 'Current members of PASS-group and their roles'
              : 'Review and manage new user access requests'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* View toggle */}
          <div className="ar-tabs">
            <button
              className={`ar-tab ${view === 'requests' ? 'ar-tab--active' : ''}`}
              onClick={() => setView('requests')}
            >
              <i className="bi bi-inbox" /> Requests
              {counts.pending > 0 && (
                <span className="ar-tab-count">{counts.pending}</span>
              )}
            </button>
            <button
              className={`ar-tab ${view === 'members' ? 'ar-tab--active' : ''}`}
              onClick={() => setView('members')}
            >
              <i className="bi bi-people" /> Members
              {members.length > 0 && (
                <span className="ar-tab-count">{members.length}</span>
              )}
            </button>
          </div>
          {/* Refresh */}
          {view === 'requests' ? (
            <button className="ar-refresh-btn" onClick={fetchRequests} disabled={loading}>
              <i className="bi bi-arrow-clockwise" /> Refresh
            </button>
          ) : (
            <button className="ar-refresh-btn" onClick={fetchMembers} disabled={membersLoading}>
              <i className="bi bi-arrow-clockwise" /> Refresh
            </button>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════
          REQUESTS VIEW
      ════════════════════════════════════ */}
      {view === 'requests' && (
        <>
          <div className="ar-toolbar">
            <div className="ar-tabs">
              {FILTERS.map(f => (
                <button
                  key={f}
                  className={`ar-tab ${filter === f ? 'ar-tab--active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                  <span className="ar-tab-count">{counts[f]}</span>
                </button>
              ))}
            </div>
            <div className="ar-search-wrap">
              <i className="bi bi-search ar-search-icon" />
              <input
                className="ar-search"
                placeholder="Search by name, email, or organization…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="ar-search-clear" onClick={() => setSearch('')}>×</button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="ar-state">
              <div className="ar-spinner" />
              <p>Loading requests…</p>
            </div>
          ) : error ? (
            <div className="ar-state ar-state--error">
              <i className="bi bi-exclamation-circle" />
              <p>{error}</p>
              <button className="ar-refresh-btn" onClick={fetchRequests}>Try again</button>
            </div>
          ) : visible.length === 0 ? (
            <div className="ar-state">
              <i className="bi bi-inbox" style={{ fontSize: '2rem', color: '#94a3b8' }} />
              <p style={{ color: '#94a3b8' }}>
                {search ? 'No results match your search.' : `No ${filter === 'all' ? '' : filter} requests.`}
              </p>
            </div>
          ) : (
            <div className="ar-table-wrap">
              <table className="ar-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Work Email</th>
                    <th>Title</th>
                    <th>Organization</th>
                    <th>Reason</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(r => (
                    <tr key={r.id} className={`ar-row ar-row--${r.status}`}>
                      <td className="ar-cell-name">{r.fullName}</td>
                      <td>
                        <a href={`mailto:${r.workEmail}`} className="ar-email-link">
                          {r.workEmail}
                        </a>
                      </td>
                      <td>{r.title || '—'}</td>
                      <td>{r.organizationName || '—'}</td>
                      <td className="ar-cell-reason">
                        {expanded === r.id ? (
                          <>
                            <span>{r.reasonForAccess}</span>
                            <button className="ar-text-btn" onClick={() => setExpanded(null)}>less</button>
                          </>
                        ) : (
                          <>
                            <span className="ar-reason-truncated">{r.reasonForAccess}</span>
                            {r.reasonForAccess?.length > 60 && (
                              <button className="ar-text-btn" onClick={() => setExpanded(r.id)}>more</button>
                            )}
                          </>
                        )}
                      </td>
                      <td className="ar-cell-date">{formatDate(r.submittedAt)}</td>
                      <td>
                        <span className={STATUS_META[r.status]?.cls || 'ar-badge ar-badge--pending'}>
                          {STATUS_META[r.status]?.label || r.status}
                        </span>
                      </td>
                      <td className="ar-cell-actions">
                        {r.status !== 'approved' && (
                          <button
                            className="ar-action-btn ar-action-btn--approve"
                            onClick={() => updateStatus(r.id, 'approved')}
                            disabled={updating === r.id}
                            title="Approve"
                          >
                            {updating === r.id ? '…' : <><i className="bi bi-check-lg" /> Approve</>}
                          </button>
                        )}
                        {r.status !== 'denied' && (
                          <button
                            className="ar-action-btn ar-action-btn--deny"
                            onClick={() => updateStatus(r.id, 'denied')}
                            disabled={updating === r.id}
                            title="Deny"
                          >
                            {updating === r.id ? '…' : <><i className="bi bi-x-lg" /> Deny</>}
                          </button>
                        )}
                        {r.status !== 'pending' && (
                          <button
                            className="ar-action-btn ar-action-btn--reset"
                            onClick={() => updateStatus(r.id, 'pending')}
                            disabled={updating === r.id}
                            title="Reset to pending"
                          >
                            {updating === r.id ? '…' : <i className="bi bi-arrow-counterclockwise" />}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="ar-count-label">
                Showing {visible.length} of {requests.length} request{requests.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════
          MEMBERS VIEW
      ════════════════════════════════════ */}
      {view === 'members' && (
        <>
          {membersLoading ? (
            <div className="ar-state">
              <div className="ar-spinner" />
              <p>Loading group members…</p>
            </div>
          ) : membersError ? (
            <div className="ar-state ar-state--error">
              <i className="bi bi-exclamation-circle" />
              <p>{membersError}</p>
              <button className="ar-refresh-btn" onClick={fetchMembers}>Try again</button>
            </div>
          ) : members.length === 0 ? (
            <div className="ar-state">
              <i className="bi bi-people" style={{ fontSize: '2rem', color: '#94a3b8' }} />
              <p style={{ color: '#94a3b8' }}>No group members found.</p>
            </div>
          ) : (
            <div className="ar-table-wrap">
              <table className="ar-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.id} className="ar-row">
                      <td className="ar-cell-name">{m.displayName}</td>
                      <td>
                        <a href={`mailto:${m.email}`} className="ar-email-link">
                          {m.email}
                        </a>
                      </td>
                      <td>
                        <span className={m.memberRole === 'admin'
                          ? 'ar-badge ar-badge--admin'
                          : 'ar-badge ar-badge--user'}>
                          {m.memberRole === 'admin' ? (
                            <><i className="bi bi-shield-check" style={{ marginRight: 4 }} />Admin</>
                          ) : (
                            <><i className="bi bi-person" style={{ marginRight: 4 }} />User</>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="ar-count-label">
                {members.length} member{members.length !== 1 ? 's' : ''} —{' '}
                {members.filter(m => m.memberRole === 'admin').length} admin,{' '}
                {members.filter(m => m.memberRole === 'user').length} user
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
